import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks';
import { ApiError } from '@/api/client';
import {
  BusyButton,
  CardIntro,
  EmailEcho,
  FieldError,
  FormAlert,
  OtpInput,
  PasswordInput,
  backCls,
  emailMsg,
  linkCls,
} from '@/components/auth-kit';

const loginSchema = z.object({
  email: z.string().superRefine((v, ctx) => {
    const m = emailMsg(v);
    if (m) ctx.addIssue({ code: 'custom', message: m });
  }),
  password: z.string().min(1, 'Enter your password.'),
  remember: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

type Alert = { title: string; body?: string } | null;

const RECOVERY_RE = /^[a-z0-9]{4}-?[a-z0-9]{4}$/i;

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<Alert>(null);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryText, setRecoveryText] = useState('');
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verifyMfa } = useAuth();
  const recoveryRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    resetField,
    setFocus,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '', remember: true },
  });

  const remember = watch('remember');
  const returnTo = searchParams.get('return_to') || '/dashboard';

  useEffect(() => {
    document.title = `${mfaStep ? 'Two-factor authentication' : 'Sign in'} · authrz.me`;
  }, [mfaStep]);

  const onSubmit = async (data: LoginFormData) => {
    setAlert(null);
    setIsLoading(true);
    try {
      const result = await login(data);
      if (result.mfaRequired) {
        setSignedInEmail(data.email.trim());
        setMfaCode('');
        setCodeInvalid(false);
        setMfaStep(true);
        return;
      }
      navigate(returnTo);
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 403) {
        setAlert({
          title: 'This account is disabled',
          body: 'Ask an organization admin or an authrz.me super admin to re-enable it.',
        });
      } else if (status === 429) {
        setAlert({
          title: 'Too many attempts',
          body: error instanceof Error ? error.message : 'Wait a moment, then try again.',
        });
      } else if (status === 0 || status >= 500) {
        setAlert({
          title: 'Couldn’t reach authrz.me',
          body: 'Check your connection and try again.',
        });
      } else {
        setAlert({
          title: 'Incorrect email or password',
          body: 'Check both and try again, or reset your password.',
        });
      }
      resetField('password');
      setFocus('password');
    } finally {
      setIsLoading(false);
    }
  };

  const submitMfa = async (code: string) => {
    setAlert(null);
    setIsLoading(true);
    try {
      await verifyMfa(code.trim());
      navigate(returnTo);
    } catch (error) {
      const expired = error instanceof ApiError && error.code === 'mfa_challenge_expired';
      setAlert(
        expired
          ? {
              title: 'Your verification session expired',
              body: 'Go back and sign in again to get a new challenge.',
            }
          : useRecovery
            ? {
                title: 'That recovery code didn’t work',
                body: 'Each recovery code works once. Check it for typos or try another.',
              }
            : {
                title: 'That code didn’t work',
                body: 'Codes change every 30 seconds. Enter the current one, or check that your device clock is correct.',
              }
      );
      if (!useRecovery) {
        setMfaCode('');
        setCodeInvalid(true);
      } else {
        recoveryRef.current?.select();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const ready = useRecovery ? RECOVERY_RE.test(recoveryText.trim()) : mfaCode.length === 6;

  const onMfaSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready || isLoading) return;
    void submitMfa(useRecovery ? recoveryText : mfaCode);
  };

  if (mfaStep) {
    return (
      <section className="flex flex-col gap-6" aria-labelledby="mfaTitle">
        <button
          type="button"
          className={backCls}
          onClick={() => {
            setMfaStep(false);
            setAlert(null);
            setMfaCode('');
            setRecoveryText('');
            setUseRecovery(false);
            resetField('password');
          }}
        >
          <ArrowLeft />
          Back to sign in
        </button>
        <CardIntro
          id="mfaTitle"
          title="Two-factor authentication"
          sub={
            <>
              {useRecovery ? 'Enter one of your recovery codes' : 'Enter the 6-digit code from your authenticator app'}
              {signedInEmail && (
                <>
                  {' '}
                  for <EmailEcho>{signedInEmail}</EmailEcho>
                </>
              )}
              .
            </>
          }
        />
        {alert && (
          <FormAlert title={alert.title}>{alert.body}</FormAlert>
        )}
        <form onSubmit={onMfaSubmit} noValidate className="flex flex-col gap-4">
          {useRecovery ? (
            <div className="space-y-1.5">
              <Label htmlFor="backupCode">Recovery code</Label>
              <Input
                id="backupCode"
                ref={recoveryRef}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                placeholder="xxxx-xxxx"
                className="h-11 font-mono"
                value={recoveryText}
                onChange={(e) => {
                  setRecoveryText(e.target.value);
                  setAlert(null);
                }}
              />
              <p className="text-[12.5px] text-muted-foreground">Each recovery code works once.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <span className="text-[13px] font-medium" id="otpLabel">
                Authentication code
              </span>
              <OtpInput
                autoFocus
                labelledBy="otpLabel"
                value={mfaCode}
                invalid={codeInvalid}
                disabled={isLoading}
                onChange={(v) => {
                  setMfaCode(v);
                  setCodeInvalid(false);
                }}
                onComplete={(v) => void submitMfa(v)}
              />
            </div>
          )}
          <BusyButton type="submit" busy={isLoading} busyLabel="Verifying…" disabled={!ready}>
            Verify
          </BusyButton>
          <button
            type="button"
            className="self-start text-[13px] text-muted-foreground underline underline-offset-[3px] hover:text-foreground"
            onClick={() => {
              setUseRecovery((u) => !u);
              setAlert(null);
              setCodeInvalid(false);
            }}
          >
            {useRecovery ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6" aria-labelledby="signInTitle">
      <CardIntro
        id="signInTitle"
        title="Sign in"
        sub="Use your authrz.me account to continue to the console."
      />
      {alert && <FormAlert title={alert.title}>{alert.body}</FormAlert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            autoComplete="username"
            autoFocus
            className="h-11"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-err' : undefined}
            {...register('email')}
          />
          <FieldError id="email-err">{errors.email?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className={`${linkCls} text-[12.5px]`}>
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            placeholder="Your password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-err' : undefined}
            {...register('password')}
          />
          <FieldError id="password-err">{errors.password?.message}</FieldError>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[13.5px]">
          <Checkbox
            id="remember"
            checked={!!remember}
            onCheckedChange={(c) => setValue('remember', !!c)}
          />
          Keep me signed in on this device
        </label>
        <BusyButton type="submit" busy={isLoading} busyLabel="Signing in…">
          Sign in
        </BusyButton>
      </form>
      <p className="text-center text-[13.5px] text-muted-foreground">
        New to authrz.me?{' '}
        <Link to="/register" className={linkCls}>
          Create an account
        </Link>
      </p>
    </section>
  );
}
